import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import {
  businessQuery,
  businessMutation,
  businessDeleteMutation,
} from '@queries';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

import { ProjectSettingsForm } from './ProjectSettingsForm/ProjectSettingsForm.jsx';

export const ProjectSettings = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // save business
  const mutation = useMutation(
    businessMutation(businessID, {
      onSuccess: (data) => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['businesses', 'detail', businessID],
        });
        // invalidate the lists so it updates
        queryClient.invalidateQueries({ queryKey: ['businesses', 'list'] });
      },
    }),
  );

  // delete datamodel
  const deleteMutation = useMutation(
    businessDeleteMutation(businessID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['businesses', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/projects`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <span>{t('Project settings')}</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-manage-content">
            <h1>{t('Project settings')}</h1>
            {businessIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!businessIsPending && (
              <>
                <ProjectSettingsForm
                  businessData={businessData}
                  disabled={mutation.isPending}
                  mutation={mutation}
                />
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Disable"
                    actionVerb="Disabling"
                    label="Disable project?"
                    note="This project will be disabled, but can be re-activated by searching"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
